import { useMediaQuery } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { darkTheme, lightTheme } from '../theme';
import { getColors } from '../theme/tokens';
import { applyCssVariables } from '../theme/utils/cssVariables';

export type ThemePreference = 'light' | 'dark' | 'automatic';

/**
 * Custom hook that manages theme preferences with user override capability.
 * Supports manual theme selection ("light", "dark") or automatic system detection.
 *
 * @returns Object containing theme, current mode, preference, and setter function
 */
export const usePreferredTheme = () => {
  // Get user preference from localStorage, default to 'automatic'
  const [userPreference, setUserPreferenceState] = useState<ThemePreference>(() => {
    try {
      const saved = localStorage.getItem('theme-preference');
      return (saved as ThemePreference) || 'automatic';
    } catch {
      return 'automatic';
    }
  });

  // Detect system preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  // Listen for localStorage changes from other components/tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'theme-preference' && e.newValue) {
        setUserPreferenceState(e.newValue as ThemePreference);
      }
    };

    // Listen for storage events (from other tabs)
    window.addEventListener('storage', handleStorageChange);

    // Also create a custom event for same-tab updates
    const handleCustomThemeChange = (e: CustomEvent) => {
      if (e.detail.preference) {
        setUserPreferenceState(e.detail.preference);
      }
    };

    window.addEventListener('themePreferenceChanged', handleCustomThemeChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themePreferenceChanged', handleCustomThemeChange as EventListener);
    };
  }, []);

  // Determine actual theme mode based on user preference and system settings
  const actualMode = useMemo(() => {
    switch (userPreference) {
      case 'light':
        return 'light';
      case 'dark':
        return 'dark';
      case 'automatic':
      default:
        return prefersDarkMode ? 'dark' : 'light';
    }
  }, [userPreference, prefersDarkMode]);

  // Select theme based on actual mode
  const theme = useMemo(() => {
    return actualMode === 'dark' ? darkTheme : lightTheme;
  }, [actualMode]);

  // Function to update user preference
  const setThemePreference = (preference: ThemePreference) => {
    setUserPreferenceState(preference);
    try {
      localStorage.setItem('theme-preference', preference);
    } catch {
      // localStorage may not be available in all environments
    }

    // Dispatch custom event for same-tab components
    window.dispatchEvent(
      new CustomEvent('themePreferenceChanged', {
        detail: { preference },
      })
    );
  };

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    const colors = getColors(actualMode);
    applyCssVariables(colors);
  }, [actualMode]);

  return {
    theme,
    isDarkMode: actualMode === 'dark',
    isAutomatic: userPreference === 'automatic',
    themePreference: userPreference,
    setThemePreference,
    actualMode,
  };
};
